import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useEffect, useState} from 'react'
import {usePrevious} from '../../../../../../commons/hooks/use-previous'
import {useAppActions, useAppSelector} from '../../../hooks/runtime'
import {isMember} from '../../../selectors/current-member-details'
import {
  allPlansPurchased as allPlansPurchasedSelector,
  getNotPurchasedPlans,
  getPaidPlanPromoDiscount,
  getPurchasablePlansWithValidityText,
  getTranslatedDetails,
  hasPurchasedPlans as hasPurchasedPlansSelector,
} from '../../../selectors/paid-plans'
import c from '../../classnames.scss'
import {ShowDetailsToggle} from '../../show-details-toggle'
import {MembershipOffersProps} from './interfaces'
import s from './membership-offers.scss'
import {Plans} from './plans'

export const MembershipOffers = ({upgrade}: MembershipOffersProps) => {
  const {t} = useTranslation()
  const {promptLogin, openMembershipPicker} = useAppActions()
  const [expanded, setExpanded] = useState(false)

  const loggedIn = useAppSelector(isMember)
  const bestOfferDiscountPercent = useAppSelector(getPaidPlanPromoDiscount)
  const hasPurchasedPlans = useAppSelector(hasPurchasedPlansSelector)
  const notPurchasedPlans = useAppSelector(getNotPurchasedPlans)
  const allPlansPurchased = useAppSelector(allPlansPurchasedSelector)
  const translatedDetails = useAppSelector(getTranslatedDetails)
  const plans = useAppSelector(getPurchasablePlansWithValidityText)
  const previousPlans = usePrevious(plans)

  useEffect(() => {
    if (plans?.length && hasPurchasedPlans) {
      toggleExpand()
    }
  }, [])

  useEffect(() => {
    if (!previousPlans?.length && hasPurchasedPlans) {
      toggleExpand()
    }
  }, [previousPlans?.length, hasPurchasedPlans])

  const toggleExpand = () => setExpanded(!expanded)

  return plans.length ? (
    <div
      className={classNames(s.wrapper, {[s.upgrade]: upgrade})}
      data-hook={DH.MEMBERSHIP_OFFERS}
      id={DH.MEMBERSHIP_OFFERS}
    >
      <div className={classNames(s.container, c.membershipOffersBorderColor, s.fontColor)}>
        <div className={s.heading}>
          <div className={classNames(s.content, s.textFont)}>
            <div className={s.primary}>{t('membership_offers')}</div>
            {bestOfferDiscountPercent && !allPlansPurchased && (
              <div className={s.secondary} data-hook={DH.MEMBERSHIP_OFFERS_PROMO}>
                {plans.length > 1
                  ? t('membership_offer_promo_multiple', {percent: bestOfferDiscountPercent})
                  : t('membership_offer_promo', {percent: bestOfferDiscountPercent})}
              </div>
            )}
          </div>
          <ShowDetailsToggle className={s.expand} expanded={expanded} onClick={toggleExpand} />
        </div>
        {expanded && (
          <div data-hook={DH.MEMBERSHIP_OFFERS_DETAILS}>
            <Plans translatedDetails={translatedDetails} plans={plans} />
            {!!notPurchasedPlans.length && (
              <div className={classNames(s.actions, c.membershipOffersBorderColor)}>
                {!loggedIn && (
                  <div className={classNames(s.login, s.textFont)}>
                    {t('membership_login_text')}
                    <button
                      className={classNames(s.button)}
                      data-hook={DH.MEMBERSHIP_OFFERS_LOGIN}
                      onClick={() => promptLogin()}
                    >
                      {t('membership_login')}
                    </button>
                  </div>
                )}
                <button
                  className={s.mainAction}
                  data-hook={DH.MEMBERSHIP_OFFERS_BUY}
                  onClick={() =>
                    openMembershipPicker({
                      titleText: t('membership_thankYou_title'),
                      contentText: t('membership_thankYou_content'),
                      buttonText: t('membership_thankYou_action'),
                    })
                  }
                >
                  {t('membership_button_buy')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  ) : null
}
